<template>
	<div
		class="side-menu"
		:class="{
			focused: store.appVisible,
			'full-sidebar': hamburgerActive,
			condensed: condensedSize,
		}"
	>
		<AccessibleSection
			name="sidebar-header"
			:tabOrder="[
				{ css: '.side-menu-header-user' },
				{ css: '.settings-wheel' },
			]"
		>
			<SidebarHeader
				:open="isOrgMenuOpen"
				@settings="goToSettings"
				@toggle="isOrgMenuOpen = !isOrgMenuOpen"
			/>
		</AccessibleSection>

		<AccessibleSection
			name="sidebar-main"
			class="side-menu-main"
			:tabOrder="sidebarTabOrder"
		>
			<div v-if="showTasks || showAnnouncements" class="sidebar-nav">
				<RouterLink
					v-if="showTasks"
					class="sidebar-nav-link radius-right task-link-wrapper"
					:class="{ active: tasksActive, unread: tasksDueCount }"
					:to="{ name: 'tasks' }"
					:draggable="false"
					@keydown.enter.prevent="$router.push('/tasks')"
				>
					<div class="sidebar-nav-icon">
						<ClipboardListCheckIcon type="regular" />
					</div>

					<div class="sidebar-nav-info">
						<div class="sidebar-nav-label" v-t="'tasks_drawer_title'" />
						<UnreadBadge
							v-if="tasksDueCount"
							class="sidebar-nav-state"
							small
							:unread="tasksDueCount"
						>
							<span>{{ $t('tasks_duecount.variable', ['']) }}</span>
						</UnreadBadge>
						<div v-if="!tasksDueCount" class="sidebar-nav-state">
							{{
								tasksDueCount > 0
									? $t(
											'tasks_duecount.variable',
											[tasksDueCount],
											tasksDueCount
									  )
									: tasksUpcomingCount > 0
									? $t(
											'tasks_upcomingcount.variable',
											[tasksUpcomingCount],
											tasksUpcomingCount
									  )
									: $t('tasks_complete')
							}}
						</div>
					</div>
				</RouterLink>
				<RouterLink
					v-if="showAnnouncements"
					class="sidebar-nav-link radius-left announcements-link-wrapper"
					:class="{
						active: announcementsActive,
						unread: unreadAnnouncementCount,
					}"
					:to="{ name: 'announcements' }"
					:draggable="false"
					@keydown.enter.prevent="$router.push('/announcements')"
				>
					<div class="sidebar-nav-icon">
						<BullhornIcon type="regular" />
					</div>

					<div class="sidebar-nav-info">
						<div
							class="sidebar-nav-label"
							v-t="'announcements_name_multiple'"
						/>

						<UnreadBadge
							v-if="unreadAnnouncementCount"
							class="sidebar-nav-state"
							small
							:unread="unreadAnnouncementCount"
						>
							<span>{{
								$t(
									'announcements_newcount.variable',
									{ 0: unreadAnnouncementCount },
									unreadAnnouncementCount
								)
							}}</span>
						</UnreadBadge>

						<div v-if="!unreadAnnouncementCount" class="sidebar-nav-state">
							{{ $t('announcements_uptodate') }}
						</div>
					</div>
				</RouterLink>

				<RouterLink
					v-if="condensedSize && hamburgerActive"
					class="sidebar-nav-settings pointer"
					:to="{ name: 'mainsettings' }"
					:aria-label="$t('common_settingsapp')"
				>
					<GearIcon class="icon-button main-settings" />
				</RouterLink>
			</div>

			<SidebarCategory :categoryId="PINNED" />
			<SidebarCategory :categoryId="RECENTS" />
			<SidebarCategory
				v-for="cat in categories"
				:key="cat.id"
				:categoryId="cat.id"
			/>
			<SidebarCategory :categoryId="OTHER" />
			<SidebarCategory :categoryId="DM" />

			<div class="spacer" />
		</AccessibleSection>
		<FAB class="fab-container" />
	</div>
</template>

<script>
import store, { getActiveCategories, getMyUserId } from '@/store'
import { getUnreadAnnouncementsCount } from '@/store/announcements'
import { UnreadBadge } from '@/components/common'
import { AccessibleSection } from '@/components/common/Accessible'
import { GearIcon, ClipboardListCheckIcon, BullhornIcon } from '@/icons'
import SidebarCategory, { PINNED, RECENTS, OTHER, DM } from './SidebarCategory'
import SidebarHeader from './SidebarHeader'
import FAB from './FAB'

export default {
	name: 'Sidebar',
	components: {
		AccessibleSection,
		UnreadBadge,
		GearIcon,
		SidebarCategory,
		SidebarHeader,
		FAB,
		ClipboardListCheckIcon,
		BullhornIcon,
	},
	props: {
		condensedSize: { type: Boolean, default: null },
		hamburgerActive: { type: Boolean, default: null },
	},
	data() {
		return {
			store,
			categories: getActiveCategories(),
			isOrgMenuOpen: false,
			unreadAnnouncementCount: 0,
			PINNED,
			RECENTS,
			OTHER,
			DM,
		}
	},
	emits: ['esc'],
	created() {
		this.$onEventBus(
			'chat-update',
			() => {
				this.categories = getActiveCategories()
			},
			25
		)

		this.$onEventBus(
			'announcement-update',
			() => {
				this.unreadAnnouncementCount = getUnreadAnnouncementsCount()
			},
			25
		)
		this.unreadAnnouncementCount = getUnreadAnnouncementsCount()
	},
	computed: {
		showTasks() {
			return store.tasksEnabled()
		},
		showAnnouncements() {
			return store.auth.user.organizations[0].announcements_enabled
		},
		tasksActive() {
			return this.$route.name === 'tasks'
		},
		announcementsActive() {
			return this.$route.name === 'announcements'
		},
		tasksDueCount() {
			return store.tasks.stats.tasksDue
		},
		tasksUpcomingCount() {
			return store.tasks.stats.tasksUpcoming
		},
		sidebarTabOrder() {
			const onEscape = async (vm, item, e) => {
				e.stopPropagation()
				await vm.$nextTick()
				this.$emit('esc', e)
				return null
			}
			return [
				{ css: '.task-link-wrapper', clickOnEnter: true, Escape: onEscape },
				{
					css: '.announcements-link-wrapper',
					clickOnEnter: true,
					Escape: onEscape,
				},
				{
					css: '.main-settings',
					clickOnEnter: true,
					Escape: onEscape,
				},
				{
					all: '.drawer-item.visible',
					preventNextOnTab: true,
					firstOfAll: '.router-link-active',
					arrows: true,
					clickOnEnter: true,
					Enter: async (vm, item, e, accessible) => {
						if (vm.$options.name === 'SidebarCategory') {
							// wait for expansion animation to complete
							await accessible.sleep(350)
							// requery to get the latest 'all' elements
							await accessible.requeryTabList()

							accessible.moveToNextElem()
						} else {
							// if selecting a new group, move focus to the input
							accessible.moveToSectionByName('chat-input')
						}
					},
					Escape: onEscape,
					Tab: () => {
						return [
							{
								css: '.global-active-element .drawer-item-menu',
								clickOnEnter: true,
							},
						]
					},
				},
			]
		},
	},
	methods: {
		goToSettings() {
			if (this.$route.name === 'mainsettings') {
				if (store.lastRoute) {
					this.$router.push(store.lastRoute)
				} else {
					this.$router.go(-1)
				}
				return
			}
			store.lastRoute = this.$route.fullPath
			this.$router.push({ name: 'mainsettings', params: { id: getMyUserId() } })
		},
	},
}
</script>

<style lang="scss" scoped>
@use 'sass:math';
@import 'src/sass/variables';

.side-menu {
	grid-area: sidebar;
	overflow-y: auto;
	overflow-x: hidden;
	overflow: hidden !important;
	display: flex;
	flex-direction: column;
	background: $drawer-gray;
	position: relative;
	&.condensed {
		background: var(--neutral-0);
	}
	.side-menu-main {
		height: 100%;
		flex: 1;
		overflow-y: auto;
		overflow-x: hidden;
	}
	.spacer {
		height: 80px;
	}
	.sidebar-nav {
		@include flex-spread;
		gap: 4px;
		padding: 8px 0;
		$link-height: 48px;
		.sidebar-nav-link {
			@include flex(8px);
			text-decoration: none;
			overflow: hidden;
			height: 48px;
			&.radius-left {
				padding: 0 8px 0 6px;
				border-top-left-radius: math.div($link-height, 2);
				border-bottom-left-radius: math.div($link-height, 2);
			}
			&.radius-right {
				border-top-right-radius: math.div($link-height, 2);
				border-bottom-right-radius: math.div($link-height, 2);
				padding: 0 20px 0 4px;
			}
			&.active {
				background: var(--teal-300);
				.sidebar-nav-info {
					.sidebar-nav-label,
					.sidebar-nav-state {
						color: var(--neutral-0);
					}
					:deep(.unread-badge) {
						color: var(--teal-300);
						background: var(--neutral-0);
						margin-left: -6px;
					}
				}
				.sidebar-nav-icon {
					color: var(--neutral-0);
					background: none;
				}
			}
			&.unread {
				.sidebar-nav-info {
					.sidebar-nav-label {
						@include fontFamily(800);
					}
				}
			}
			&:hover:not(.active) {
				background: var(--neutral-200);
			}
			.sidebar-nav-info {
				@include flex-column;
				align-items: flex-start;
				.sidebar-nav-label {
					font-size: 12px;
					color: var(--neutral-700);
				}
				.sidebar-nav-state {
					color: var(--neutral-500);
					font-size: 10px;
					white-space: nowrap;
				}
				:deep(.unread-badge) {
					color: var(--neutral-0);
				}
			}
			.sidebar-nav-icon {
				@include flex-center;
				font-size: 18px;
				width: 40px;
				height: 40px;
				border-radius: 50%;
				overflow: hidden;
				color: var(--neutral-700);
				background: var(--neutral-0);
				flex-shrink: 0;
			}
		}
		.sidebar-nav-settings {
			padding: 16px;
			font-size: 18px;
			color: var(--neutral-700);
		}
	}
}
</style>
<style lang="scss">
@import 'src/sass/variables';

@include overrideScheme('dark') {
	.side-menu {
		background: var(--neutral-0);
		.sidebar-nav {
			.sidebar-nav-link {
				&.active {
					background: var(--neutral-300) !important;
					.sidebar-nav-icon {
						background: none !important;
					}
				}
				.sidebar-nav-icon {
					color: var(--neutral-800) !important;
				}
				.sidebar-nav-info {
					.sidebar-nav-label {
						color: var(--neutral-800) !important;
					}
					.sidebar-nav-state {
						color: var(--neutral-800) !important;
					}
				}
			}
		}
	}
}
</style>
